/**
 * Preferências locais (SPEC 10.8): `localStorage` guarda APENAS preferências —
 * tema, densidade, fonte, sidebar, inspector, atalhos de uma tecla, visões salvas,
 * última visita e o papel de demonstração ("Ver como"). Nunca dado pessoal.
 *
 * Toda leitura e escrita é protegida: em janela privada, com o storage bloqueado
 * ou cheio, o app segue funcionando com os padrões.
 */
import type { CompareMode, DateRange, Filters, Ms, Role } from '@rota/contracts';

export type ThemePref = 'system' | 'light' | 'dark';
export type DensityPref = 'comfortable' | 'compact';
export type FontPref = 'auto' | 'inter';
/** Vidro líquido (29/10/2026): intensidade do efeito; `off` = superfícies opacas. */
export type GlassPref = 'off' | 'sutil' | 'medio' | 'intenso';

export interface SavedView {
  id: string;
  name: string;
  /** Rota onde a visão foi salva, ex. "/trafego". */
  path: string;
  /** Query string canônica (p, de, ate, cmp, f, nivel, ord). */
  search: string;
  range?: Pick<DateRange, 'preset'>;
  compare?: CompareMode;
  filters?: Filters;
  createdAt: Ms;
}

export interface Prefs {
  theme: ThemePref;
  /** `null` = padrão do papel (traffic = compacta; 2.3). */
  density: DensityPref | null;
  font: FontPref;
  glass: GlassPref;
  sidebarCollapsed: boolean;
  /** `null` = padrão por papel e largura (owner/traffic ≥ 1600 px; 3.2). */
  inspectorOpen: boolean | null;
  singleKeyShortcuts: boolean;
  savedViews: SavedView[];
  lastVisitAt: Ms | null;
  /** "Ver como" persistido na demonstração da V1. */
  viewAs: Role | null;
}

export const PREF_DEFAULTS: Readonly<Prefs> = Object.freeze({
  theme: 'system',
  density: null,
  font: 'auto',
  glass: 'medio',
  sidebarCollapsed: false,
  inspectorOpen: null,
  singleKeyShortcuts: true,
  savedViews: [],
  lastVisitAt: null,
  viewAs: null,
});

const PREFIX = 'rota:';
const keyOf = (k: keyof Prefs): string => `${PREFIX}${k}`;

const ROLES: readonly Role[] = ['owner', 'partner', 'traffic', 'creative', 'support', 'expert', 'tech', 'tv'];

/** Validadores: um valor corrompido ou de outra versão volta ao padrão. */
const validators: { [K in keyof Prefs]: (v: unknown) => v is Prefs[K] } = {
  theme: (v): v is ThemePref => v === 'system' || v === 'light' || v === 'dark',
  density: (v): v is DensityPref | null => v === null || v === 'comfortable' || v === 'compact',
  font: (v): v is FontPref => v === 'auto' || v === 'inter',
  glass: (v): v is GlassPref => v === 'off' || v === 'sutil' || v === 'medio' || v === 'intenso',
  sidebarCollapsed: (v): v is boolean => typeof v === 'boolean',
  inspectorOpen: (v): v is boolean | null => v === null || typeof v === 'boolean',
  singleKeyShortcuts: (v): v is boolean => typeof v === 'boolean',
  savedViews: (v): v is SavedView[] =>
    Array.isArray(v) &&
    v.every(
      (x: unknown) =>
        typeof x === 'object' &&
        x !== null &&
        typeof (x as SavedView).id === 'string' &&
        typeof (x as SavedView).name === 'string' &&
        typeof (x as SavedView).path === 'string' &&
        typeof (x as SavedView).search === 'string',
    ),
  lastVisitAt: (v): v is Ms | null => v === null || (typeof v === 'number' && Number.isFinite(v)),
  viewAs: (v): v is Role | null =>
    v === null || (typeof v === 'string' && (ROLES as readonly string[]).includes(v)),
};

function store(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Lê uma preferência; ausente, inválida ou inacessível → padrão. */
export function getPref<K extends keyof Prefs>(key: K): Prefs[K] {
  const s = store();
  if (!s) return PREF_DEFAULTS[key];
  try {
    const raw = s.getItem(keyOf(key));
    if (raw === null) return PREF_DEFAULTS[key];
    const parsed: unknown = JSON.parse(raw);
    return validators[key](parsed) ? parsed : PREF_DEFAULTS[key];
  } catch {
    return PREF_DEFAULTS[key];
  }
}

type Listener = (key: keyof Prefs) => void;
const listeners = new Set<Listener>();

/** Grava uma preferência. Valores iguais ao padrão são removidos (storage enxuto). */
export function setPref<K extends keyof Prefs>(key: K, value: Prefs[K]): void {
  const s = store();
  if (s) {
    try {
      if (JSON.stringify(value) === JSON.stringify(PREF_DEFAULTS[key])) s.removeItem(keyOf(key));
      else s.setItem(keyOf(key), JSON.stringify(value));
    } catch {
      // storage cheio ou bloqueado: a preferência vale só nesta sessão
    }
  }
  for (const l of listeners) l(key);
}

/** Todas as preferências de uma vez (boot). */
export function readPrefs(): Prefs {
  return {
    theme: getPref('theme'),
    density: getPref('density'),
    font: getPref('font'),
    glass: getPref('glass'),
    sidebarCollapsed: getPref('sidebarCollapsed'),
    inspectorOpen: getPref('inspectorOpen'),
    singleKeyShortcuts: getPref('singleKeyShortcuts'),
    savedViews: getPref('savedViews'),
    lastVisitAt: getPref('lastVisitAt'),
    viewAs: getPref('viewAs'),
  };
}

/** Observa mudanças (nesta aba e em outras abas, via evento `storage`). */
export function onPrefChange(listener: Listener): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent): void => {
    if (!e.key?.startsWith(PREFIX)) return;
    const k = e.key.slice(PREFIX.length) as keyof Prefs;
    if (k in validators) listener(k);
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

/** Aplica tema, densidade e fonte ao `<html>` (o script do `<head>` faz o mesmo antes do CSS). */
export function applyDocumentPrefs(
  prefs: Pick<Prefs, 'theme' | 'font'> & { density: DensityPref },
  doc: Document = document,
): void {
  const root = doc.documentElement;
  if (prefs.theme === 'system') delete root.dataset.theme;
  else root.dataset.theme = prefs.theme;
  root.dataset.density = prefs.density;
  if (prefs.font === 'inter') root.dataset.font = 'inter';
  else root.dataset.font = isApplePlatform() ? 'sf' : 'inter';
  root.dataset.glass = getPref('glass');
}

/** Aparelho Apple renderiza o SF Pro nativo via -apple-system (9.3). */
export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Macintosh|Mac OS X|iPhone|iPad|iPod/.test(ua);
}
